import { Group, Stack, Table, Text } from '@mantine/core'
import { FormattedMessage } from 'react-intl'
import { OnlineStatus } from '@/components/devices/OnlineStatus/OnlineStatus'
import { DataTable } from '@/components/ui-shared/Tables/DataTable'
import { Pipeline, PipelineStatus } from '@/types/vlm'
import { PipelineActionList } from './PipelineActionList'
import { PipelineAlertTags } from './PipelineAlertTags'
import { PipelineInputSource } from './PipelineInputSource'
import { PipelineOutputList } from './PipelineOutputList'
import { VlmPipelineListActions } from './VlmPipelineListActions'
import { VlmPipelineStatusBadge } from './VlmPipelineStatusBadge'

type VlmPipelineListProps = {
  pipelines: Pipeline[]
  isLoading?: boolean
  hasError?: boolean
  hasVlmAgentsAccess: boolean
  onReload?: () => void
  onEdit: (pipeline: Pipeline) => void
  onDelete: (pipeline: Pipeline) => void
  onRestart: (pipeline: Pipeline) => void
}

export const VlmPipelineList = ({
  pipelines,
  isLoading,
  hasError,
  hasVlmAgentsAccess,
  onReload,
  onEdit,
  onDelete,
  onRestart
}: VlmPipelineListProps) => {
  return (
    <DataTable
      hasItems={pipelines.length > 0}
      isLoading={isLoading}
      hasError={hasError}
      emptyMessage={<FormattedMessage id="vlm.pipelines.empty" />}
      onReload={onReload}
    >
      <Table.Thead>
        <Table.Tr>
          <Table.Th>
            <FormattedMessage id="vlm.pipelines.fields.name" />
          </Table.Th>
          <Table.Th>
            <FormattedMessage id="vlm.pipelines.fields.device" />
          </Table.Th>
          <Table.Th>
            <FormattedMessage id="vlm.pipelines.fields.inputSource" />
          </Table.Th>
          <Table.Th>
            <FormattedMessage id="vlm.pipelines.fields.alerts" />
          </Table.Th>
          <Table.Th>
            <FormattedMessage id="vlm.pipelines.fields.actions" />
          </Table.Th>
          <Table.Th>
            <FormattedMessage id="vlm.pipelines.fields.output" />
          </Table.Th>
          <Table.Th>
            <FormattedMessage id="vlm.pipelines.fields.status" />
          </Table.Th>
          <Table.Th>&nbsp;</Table.Th>
        </Table.Tr>
      </Table.Thead>

      <Table.Tbody>
        {pipelines.map((pipeline) => (
          <Table.Tr key={pipeline.id}>
            <Table.Td fw={600}>{pipeline.name}</Table.Td>

            <Table.Td>
              <Group gap={6} wrap="nowrap" align="center">
                <OnlineStatus
                  status={pipeline.device_active ? 'online' : 'offline'}
                  withText={false}
                />
                <Text
                  size="xs"
                  c={pipeline.device_active ? undefined : 'dimmed'}
                >
                  {pipeline.device_name}
                </Text>
              </Group>
            </Table.Td>

            <Table.Td>
              <PipelineInputSource source={pipeline.source} />
            </Table.Td>

            <Table.Td>
              <PipelineAlertTags alertPrompts={pipeline.alert_prompts} />
            </Table.Td>

            <Table.Td>
              <PipelineActionList actions={pipeline.actions} />
            </Table.Td>

            <Table.Td>
              <PipelineOutputList
                source={pipeline.source}
                enableCloudResults={pipeline.enable_cloud_results}
              />
            </Table.Td>

            <Table.Td>
              <Stack gap={4}>
                <VlmPipelineStatusBadge status={pipeline.status} />
                {(pipeline.status === PipelineStatus.Failed ||
                  pipeline.status === PipelineStatus.Degraded) &&
                  pipeline.error_message && (
                    <Text size="xs" c="red" maw={300}>
                      {pipeline.error_message}
                    </Text>
                  )}
              </Stack>
            </Table.Td>

            <Table.Td>
              <VlmPipelineListActions
                pipeline={pipeline}
                hasVlmAgentsAccess={hasVlmAgentsAccess}
                onEdit={() => onEdit(pipeline)}
                onDelete={() => onDelete(pipeline)}
                onRestart={() => onRestart(pipeline)}
              />
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </DataTable>
  )
}
